import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useIsAccountingExportMode} from '../feature-flags/useIsAccountingExportMode';
import {accountingActions} from '../../state/actions/accounting.actions';
import {accountingHelpers} from '../../utils/helpers/accounting.helpers';
import {customFieldTypeConstants} from '../../constants/accounting.constants';

const {DROPDOWN} = customFieldTypeConstants;

const CUSTOM_FIELD_COLUMN_WIDTH = 180;

/**
 * Which key the agent filed its suggestion under for this field.
 *
 * The suggestions map is keyed by whatever the agent calls the field, and for a custom field that
 * is either its id or its user-facing name — the requirements do not say, and dev has no generated
 * suggestions to read it off (the request queues but stays `pending`). Rather than guess, this
 * accepts either and falls back to the id.
 *
 * The resolved key is passed as `propName`, which is deliberate: the same value is what
 * SuggestionTableField sends back as `field_name` when the user overrides, so the lookup and the
 * feedback can never disagree about what the field is called.
 */
const suggestionKeyFor = (record, field) => {
  const map = record?.agent_suggestion?.suggestions;
  if (map?.[field.id] !== undefined) return field.id;
  if (map?.[field.name] !== undefined) return field.name;
  return field.id;
};

/**
 * How a dropdown option reads, everywhere it is read.
 *
 * A dropdown value has two parts: the ERP's own code and the in-product label. Confluence says the
 * External ID is "only available on the exports", but Enric demoed NetSuite concatenating a code
 * and a description in the table — so this shows both, the way the product the customer is
 * migrating from already does.
 *
 * ⚠ This is OUR decision, not a stated requirement (see D12 in the plan). It is one function on
 * purpose: the cell and the option list must never disagree, and correcting it is one line. The
 * stored value stays the bare `external_id`, which is what the export emits and what the ERP
 * matches on — only the presentation is joined.
 */
const getCustomFieldOptionLabel = ({external_id: externalId, value}) =>
  [externalId, value].filter(Boolean).join(' — ');

/**
 * Table columns for the company's own ERP fields, one per definition that applies to this object.
 *
 * Returns [] outside export mode, so the three accounting tables can spread it unconditionally and
 * a NetSuite company sees no change at all.
 *
 * ⚠ Each edit is its own request — the API takes one field per call, with no batch form — so this
 * saves per cell rather than collecting a row.
 *
 * @param {Object} params
 * @param {string} params.recordType - vendor | vendor_bill | journal_entry (SuggestionRecordType).
 * @param {Function} [params.onSaved] - Called with ({id, fieldId, value}) after a successful save,
 *   so the caller can update its own row state without refetching the list.
 * @returns {Array<Object>} Ant Table column definitions.
 *
 * @example
 * const customColumns = useAccountingCustomFieldColumns({recordType: 'journal_entry', onSaved});
 * const columns = [...baseColumns, ...customColumns];
 */
export const useAccountingCustomFieldColumns = ({recordType, onSaved} = {}) => {
  const [t] = useTranslation('main');
  const dispatch = useDispatch();
  const isExportMode = useIsAccountingExportMode();

  const fields = useSelector(state => state.accounting?.accountingFields ?? []);

  const applicable = useMemo(
    () => (isExportMode ? fields.filter(field => (field.applies_to || []).includes(recordType)) : []),
    [fields, isExportMode, recordType],
  );

  const save = useCallback((record, field, value) => {
    dispatch(accountingActions.updateCustomFieldValue({
      recordType,
      id: record.id,
      fieldId: field.id,
      value,
      // ⚠ The MERGED map is handed back, not the single field. `render` is the only place that holds
      // the record, and six pages each merging this themselves is exactly how they drift apart
      // (NS-8). The caller just drops it into its row.
      successCallback: () => onSaved?.({
        id: record.id,
        customFieldValues: {...(record.custom_field_values || {}), [field.id]: String(value)},
      }),
      // ⚠ Without this the write fails in complete silence: the action sets `disableAlert: true`
      // and the request layer mutes 401/403 anyway, so a rejected value sits in the cell looking
      // saved and Register stays blocked with no clue why (H1MVP-4800).
      errorCallback: () => accountingHelpers.showAlert(
        'error',
        t('accounting:validation.failedToSaveCustomField', {field: field.name}),
      ),
    }));
  }, [dispatch, recordType, onSaved, t]);

  return useMemo(() => applicable.map((field) => {
    const isDropdown = field.field_type === DROPDOWN;

    return {
      // The values arrive as a map keyed by field id, so the column reads straight into it.
      dataIndex: ['custom_field_values', field.id],
      key: field.id,
      title: field.name,
      width: CUSTOM_FIELD_COLUMN_WIDTH,
      ...accountingHelpers.getEllipsisColumnProps(CUSTOM_FIELD_COLUMN_WIDTH),
      render: (defaultValue, record) => {
        if (isDropdown) {
          return accountingHelpers.getTableSelect({
            defaultValue,
            options: (field.values || []).map(value => ({
              label: getCustomFieldOptionLabel(value),
              value: value.external_id,
            })),
            propName: suggestionKeyFor(record, field),
            record,
            t,
            onSelect: (value) => save(record, field, value),
            // The field name is the admin's own text, not a translation key, so the generated
            // placeholder has to be overridden rather than looked up.
            placeholder: t('placeholderTemplates.select', {name: field.name}),
          });
        }

        // ⚠ NO `type: 'number'`, even for a number-declared field (H1MVP-4806). Measured on dev, and
        // the server settles it (NS-5):
        //
        //   PATCH a NUMBER-declared field with "0410"   -> 200, stored as "0410"  (zero KEPT)
        //                                    with "7"        -> 200
        //                                    with "04-A"     -> 400
        //                                    with "12.5.3"   -> 400
        //
        // So the API validates the content but preserves a leading zero, which is exactly right for
        // an ERP code — 0410 and 410 are different accounts. The widgets were the problem, in two
        // opposite directions. The modal's `InputNumber` turned "0410" into "410", and the server
        // would have accepted that happily as a different, wrong value: silent corruption of a
        // VALID entry, which is the worse of the two. This inline `type="number"` discarded instead
        // — "Bulk 2" left "2", and "04-A" and "12.5.3" left the cell EMPTY, so a user could not even
        // see the entry the server was going to refuse.
        //
        // Plain text lets the user type the code, keeps it on screen, and lets the server be the
        // one to accept or refuse it. A 400 surfaces through the errorCallback added in H1MVP-4800,
        // so a refusal is now stated rather than performed silently by the input.
        return accountingHelpers.getTableInput({
          defaultValue,
          propName: suggestionKeyFor(record, field),
          record,
          t,
          onChange: (value) => save(record, field, value),
          placeholder: t('placeholderTemplates.enter', {name: field.name}),
        });
      },
    };
  }), [applicable, save, t]);
};

export {getCustomFieldOptionLabel};
